import Instance from './instance.js'
import { different, isSet, notSet, isObject, hasOwn, clone, isNumber, isArray, compilePattern } from '../helpers/utils.js'
import {
  getSchemaAdditionalProperties,
  getSchemaDependentRequired,
  getSchemaPatternProperties,
  getSchemaProperties,
  getSchemaRequired,
  getSchemaXOption
} from '../helpers/schema.js'

/**
 * Represents an InstanceObject instance.
 * @extends Instance
 */
class InstanceObject extends Instance {
  prepare () {
    this.properties = {}
    this.requiredProperties = new Set()
    this.schemaPatternProperties = getSchemaPatternProperties(this.schema)
    this.schemaAdditionalProperties = getSchemaAdditionalProperties(this.schema)

    const schemaProperties = getSchemaProperties(this.schema)
    const schemaRequired = getSchemaRequired(this.schema)

    const initialValue = clone(this.value)

    if (isSet(schemaProperties)) {
      Object.keys(schemaProperties).forEach((key) => {
        let schema = schemaProperties[key]

        // Expand a property's own $ref so x-options declared on the referenced
        // schema (e.g. x-defaultProperty) are visible before the create/activate
        // decision below (fixes issue #78, mirrors the array items fix for #24).
        if (isObject(schema) && this.jedison.refParser && this.jedison.refParser.hasRef(schema) && !schema['x-recursive']) {
          schema = this.jedison.refParser.expand(schema)
          schemaProperties[key] = schema
        }

        this.properties[key] = { schema }

        let musstCreateChild = true

        const isRecursive = isSet(schema['x-recursive'])
        const optionsDeactivateNonRequired = this.jedison.getOption('deactivateNonRequired')
        const deactivateNonRequired = getSchemaXOption(this.schema, 'deactivateNonRequired')
        const schemaDeactivateNonRequired = getSchemaXOption(schema, 'deactivateNonRequired')

        const isReq = this.isRequired(key)

        if (!isReq && isSet(optionsDeactivateNonRequired) && optionsDeactivateNonRequired === true) {
          musstCreateChild = false
        }

        if (!isReq && isSet(deactivateNonRequired) && deactivateNonRequired === true) {
          musstCreateChild = false
        }

        if (!isReq && isSet(schemaDeactivateNonRequired) && schemaDeactivateNonRequired === true) {
          musstCreateChild = false
        }

        if (!isReq && isRecursive) {
          musstCreateChild = false
        }

        const defaultProperties = getSchemaXOption(this.schema, 'defaultProperties')
        const hasDefaultProperties = isArray(defaultProperties)
        const propertyDefaultProperty = getSchemaXOption(schema, 'defaultProperty')
        const hasPropertyDefaultProperty = isSet(propertyDefaultProperty)

        // A property's own x-defaultProperty always wins over the parent's list -
        // this is the override point for properties reused (e.g. via $ref) across
        // parents that disagree on whether it should show by default.
        const isDefaultProperty = hasPropertyDefaultProperty
          ? propertyDefaultProperty === true
          : (hasDefaultProperties && defaultProperties.includes(key))

        // Declaring either form of "default properties" turns this into a whitelist:
        // a non-required property that doesn't resolve as default is hidden,
        // regardless of deactivateNonRequired.
        if (!isReq && (hasDefaultProperties || hasPropertyDefaultProperty) && !isDefaultProperty) {
          musstCreateChild = false
        }

        if (!isReq && !isRecursive && isDefaultProperty) {
          musstCreateChild = true
        }

        if (musstCreateChild) {
          this.createChild(schema, key, hasOwn(initialValue, key) ? initialValue[key] : undefined, isDefaultProperty)
        }
      })
    }

    // Add properties listed in schema required too if not present in schema properties
    if (isSet(schemaRequired) && this.jedison.isEditor && this.jedison.getOption('enforceRequired') === true) {
      schemaRequired.forEach((requiredProperty) => {
        this.requiredProperties.add(requiredProperty)

        if (!hasOwn(this.properties, requiredProperty)) {
          this.properties[requiredProperty] = {}
          this.createChild({}, requiredProperty, hasOwn(initialValue, requiredProperty) ? initialValue[requiredProperty] : undefined)
        }
      })
    }

    this.refreshInstances()

    this.on('set-value', (value, initiator) => {
      this.addMissingRequiredPropertiesToValue(value)
      this.removeNotListedPropertiesFromValue(value)
      this.refreshInstances(initiator)
    })
  }

  removeNotListedPropertiesFromValue (value) {
    const schemaEnforceAdditionalProperties = getSchemaXOption(this.schema, 'enforceAdditionalProperties')
    const enforceAdditionalProperties = isSet(schemaEnforceAdditionalProperties) ? schemaEnforceAdditionalProperties : this.jedison.getOption('enforceAdditionalProperties')
    const schemaAdditionalProperties = this.schemaAdditionalProperties
    const schemaPatternProperties = this.schemaPatternProperties || {}

    if (this.jedison.isEditor && enforceAdditionalProperties && isSet(schemaAdditionalProperties) && schemaAdditionalProperties === false) {
      const compiledPatterns = Object.keys(schemaPatternProperties).map(p => compilePattern(p))
      Object.keys(value).forEach((propertyName) => {
        const matchesPattern = compiledPatterns.some(re => re.test(propertyName))

        if (!hasOwn(this.properties, propertyName) && !matchesPattern) {
          delete value[propertyName]
        }
      })
    }
  }

  addMissingRequiredPropertiesToValue (value) {
    const enforceRequired = getSchemaXOption(this.schema, 'enforceRequired') ?? this.jedison.getOption('enforceRequired')

    if (this.jedison.isEditor && enforceRequired) {
      this.requiredProperties.forEach((propertyName) => {
        if (!hasOwn(value, propertyName)) {
          value[propertyName] = this.getChild(propertyName).getValue()
        }
      })
    }
  }

  /**
   * Returns true if the property is required
   */
  isRequired (property) {
    const schemaRequired = getSchemaRequired(this.schema)
    const inSchemaRequired = isSet(schemaRequired) && schemaRequired.includes(property)
    const inSchemaDependentRequired = this.isDependentRequired(property)

    return inSchemaRequired || inSchemaDependentRequired
  }

  /**
   * Returns true if the property is dependent required
   */
  isDependentRequired (property) {
    const dependentRequired = getSchemaDependentRequired(this.schema)

    if (isSet(dependentRequired)) {
      let missingProperties = []

      Object.keys(dependentRequired).forEach((key) => {
        if (isSet(this.value[key])) {
          const requiredProperties = dependentRequired[key]

          if (isArray(requiredProperties)) {
            missingProperties = requiredProperties.filter((property) => {
              return !hasOwn(this.value, property)
            })
          }
        }
      })

      return missingProperties.includes(property)
    }

    return false
  }

  createChild (schema, key, value, activate = false) {
    const instance = this.jedison.createInstance({
      jedison: this.jedison,
      schema: schema,
      path: this.path + this.jedison.pathSeparator + key,
      parent: this,
      value: clone(value)
    })

    this.children.push(instance)
    this.value[key] = instance.getValue()

    const deactivateNonRequired = getSchemaXOption(this.schema, 'deactivateNonRequired') ?? this.jedison.getOption('deactivateNonRequired')

    if (!this.isRequired(key) && isSet(deactivateNonRequired) && deactivateNonRequired === true && !activate) {
      instance.deactivate()
    }

    this.onChildChange()

    return instance
  }

  deleteChild (key) {
    for (let i = this.children.length - 1; i >= 0; i--) {
      const instance = this.children[i]
      if (instance.getKey() === key) {
        instance.destroy()
        this.children.splice(i, 1)
        this.onChildChange()
      }
    }
  }

  getChild (key) {
    return this.children.find((instance) => {
      return key === instance.getKey().split(this.jedison.pathSeparator).pop()
    })
  }

  getPropertySchema (propertyName) {
    let schema
    const schemaAdditionalProperties = this.schemaAdditionalProperties
    const schemaProperties = getSchemaProperties(this.schema)
    const schemaPatternProperties = this.schemaPatternProperties

    // Determine the appropriate schema
    if (isSet(schemaProperties) && hasOwn(schemaProperties, propertyName)) {
      // If the propertyName is explicitly defined in `properties`, use it
      schema = schemaProperties[propertyName]
    } else if (isSet(schemaPatternProperties)) {
      // If no exact match in `properties`, check if it matches any pattern in `patternProperties`
      for (const pattern of Object.keys(schemaPatternProperties)) {
        if (compilePattern(pattern).test(propertyName)) {
          schema = schemaPatternProperties[pattern]
          break
        }
      }
    }

    if (notSet(schema) && isSet(schemaAdditionalProperties)) {
      // If no match was found in `properties` or `patternProperties`, use `additionalProperties`
      schema = schemaAdditionalProperties
    }

    if (notSet(schema)) {
      schema = {}
    }

    return schema
  }

  onChildChange (initiator) {
    const value = {}

    this.children.forEach((child) => {
      if (child.isActive) {
        const propertyName = child.getKey()

        if (propertyName === '__proto__') {
          Object.defineProperty(value, propertyName, {
            value: child.getValueRaw(),
            enumerable: true
          })
        } else {
          value[propertyName] = child.getValueRaw()
        }
      }
    })

    this.value = value
    this.jedison.emit('instance-change', this, initiator)
    this.emit('change', initiator)

    // Suppress upward propagation while this object is rebuilding its children.
    // Intermediate notifications would cause ancestors to re-walk the tree once
    // per child created, leading to O(n²) work for large arrays. The flag is
    // cleared by refreshInstances() after all children are settled, at which
    // point setValue() emits its own notifyParent exactly once.
    if (!this.refreshingInstances) {
      this.emit('notifyParent', initiator)
    }
  }

  /**
   * Sorts the children of the current instance based on their `propertyOrder` value in ascending order.
   * The sorting is done using the `propertyOrder` obtained from each child's schema, which should be a number.
   * If a child does not have a valid `propertyOrder` (i.e., the value is not a number), it will be placed after the child with a valid `propertyOrder`.
   * @returns {void} This function modifies the `children` array of the instance in place.
   */
  sortChildrenByPropertyOrder () {
    this.children = this.children.sort((a, b) => {
      const propertyOrderA = getSchemaXOption(a.schema, 'propertyOrder')
      const propertyOrderB = getSchemaXOption(b.schema, 'propertyOrder')

      const isValidNumberA = isNumber(propertyOrderA)
      const isValidNumberB = isNumber(propertyOrderB)

      if (!isValidNumberA && isValidNumberB) {
        return 1
      }

      if (isValidNumberA && !isValidNumberB) {
        return -1
      }

      if (propertyOrderA < propertyOrderB) {
        return -1
      }

      if (propertyOrderA > propertyOrderB) {
        return 1
      }

      return 0
    })
  }

  refreshInstances (initiator) {
    // Save and restore rather than set/clear so that re-entrant calls (nested
    // objects calling refreshInstances on their own children) do not prematurely
    // lower the flag while an outer call is still in progress.
    const wasRefreshing = this.refreshingInstances
    this.refreshingInstances = true

    const value = this.getValue()

    if (!isObject(value)) {
      this.refreshingInstances = wasRefreshing
      return
    }

    // Build child lookup map once to avoid repeated linear scans
    const childMap = new Map()
    for (const child of this.children) {
      childMap.set(child.getKey(), child)
    }

    Object.keys(value).forEach((propertyName) => {
      const child = childMap.get(propertyName)

      // If a value already has a child instance
      if (child) {
        child.activate()
        const oldValue = child.getValueRaw()
        const newValue = value[propertyName]

        // update child value if the old value and the new value are different
        if (different(oldValue, newValue)) {
          const finalValue = child.setValue(newValue, false, initiator)
          value[propertyName] = finalValue
        }
      } else {
        // create new child instance for the new value entry having the value as default
        const schema = this.getPropertySchema(propertyName)

        this.createChild(schema, propertyName, value[propertyName], true)
      }
    })

    const keepMissingSchemaFields = this.jedison.getOption('keepMissingSchemaFields')

    for (let i = this.children.length - 1; i >= 0; i--) {
      const instance = this.children[i]
      const propertyName = instance.getKey()
      if (notSet(value[propertyName])) {
        const isSchemaProperty = hasOwn(this.properties, propertyName)
        if (isSchemaProperty && keepMissingSchemaFields) {
          continue
        }
        if (childMap.has(propertyName)) {
          instance.deactivate()
        } else {
          this.deleteChild(propertyName)
        }
      }
    }

    this.sortChildrenByPropertyOrder()

    // Update the object's value with the corrected values after constraint enforcement
    this.value = value
    this.refreshingInstances = wasRefreshing // restore so onChildChange can emit notifyParent again
  }
}

export default InstanceObject
